import {DetailPaymentClassType} from '@web/core/constants'

import {Pay} from './pay'
import {
    Account,
    Card,
    FamilyPay,
    Mobile,
    RechargePoint,
    Rental,
    Skip,
    Subscription,
    VirtualAccount,
} from './paymentMethod'

/** 상세 내 결제관련 정보를 관리하는 Slice State */
export interface AdmissionDetailState {
    /** 결제 번호 */
    id: string
    /** 결제 일시 (ISO 형식) */
    date: string
    /** 결제 상태 */
    status: PaymentStatusType
    /** 사용된 결제수단 정보 */
    usedPaymentMethod: {
        card?: Card
        account?: Account
        subscription?: Subscription
        rental?: Rental
        familyPay?: FamilyPay
        recharge?: RechargePoint
        mobile?: Mobile
        virtualAccount?: VirtualAccount
        skip?: Skip
    }
    /** 결제 관련 정보 */
    pay: Pay
    /** 영수증 정보 */
    receipts: Array<{
        /** 영수증 타입 */
        type: ReceiptTypeType
        /** 영수증 URL */
        url: Maybe<string>
    }>
    /** 환불 정보 */
    refund?: {
        /** 환불 방법 (ex. 원결제 취소) */
        refundMethod?: string
        /** 미결제 취소 여부 */
        noPayCanceled?: boolean
        /** 환불 금액 정보 */
        amount: {
            /** 환불 총 금액 */
            totalAmount: number
            /** 상품 금액 */
            productAmount: number
            /** 차감 금액 (ex. 할인) */
            deductedAmount: number
            /** 환불정산액 환불액 */
            alternativeAmount: number
            /** 결제 인증 유형 */
            authenticationType?: Extract<DetailPaymentClassType, 'SAMSUNG_PAY' | 'APPLE_PAY' | 'NONE'>
            /** 결제수단 별 사용 환불 금액정보 */
            amountByPaymentMethod: {
                /** 주결제 금액 */
                generalPayAmount: number
                /** 주결제수단 타입 */
                generalPayMeansClassType: Maybe<DetailPaymentClassType>
                /** 적립포인트 환불액 */
                rewardPointAmount: number
                /** 충전포인트 환불액 */
                chargedPointAmount: number
                /** 충전금액 환불액 (환불정산액 환불) */
                chargeAmount: number
                /** 카드프로모션 포인트 환불액 */
                cardPromotionPointAmount: number
                /** 카드환불액 */
                cardAmount: number
                /** 은행 환불액 */
                bankAmount: number
                /** 무통장입금 환불액 */
                virtualAccountAmount: number
                /** 휴대폰결제 환불액 */
                mobileAmount: number
                /** 나중에결제 환불액 */
                payLaterAmount: number
                /** 패밀리결제 환불액 */
                familyPayAmount: number
                /** 기프트카드 환불액 */
                giftCardAmount: number
                /** 외부 바우처 */
                externalVouchers?: {
                    /** 바우처타입  */
                    type: ExternalVoucherTypeType
                    /** 외부바우처 환불금액 */
                    amount: number
                }[]
            }
            /** 환불 보증금 모델 */
            deposit?: {
                /** 쿨백 보증금환불액 */
                coolBag: number
                /** 재사용백 보증금환불액 */
                reusableBag: number
                /** 일회용컵 보증금환불액 */
                singleUseCup: number
                /** 재사용컵 보증금환불액 */
                reusableCup: number
                /** 봉투값 */
                plasticBag: number
            }
        }
    }
    /** 혜택 정보 */
    benefit?: {
        /** 포인트 뽑기 카드 존재 여부 */
        hasPointPick: boolean
    }
}

/** 결제 상태 */
export const PaymentStatus = {
    /** 결제 완료 */
    COMPLETION: 'COMPLETION',
    /** 부분 결제 완료 (부분 취소) */
    PARTIAL_COMPLETION: 'PARTIAL_COMPLETION',
    /** 결제 취소 */
    CANCELLATION: 'CANCELLATION',
} as const
export type PaymentStatusType = Constant<typeof PaymentStatus>

/** 영수증 종류 */
export const ReceiptType = {
    /** 현금 영수증 */
    CASH: 'CASH',
    /** 카드 영수증 */
    CARD: 'CARD',
    /** 컵 보증금 영수증 */
    CUP_DEPOSIT: 'CUP_DEPOSIT',
    /** 온라인 영수증 */
    ONLINE: 'ONLINE',
    /** 포인트/머니 온라인 영수증 */
    POINT: 'POINT',
    /** 기타 */
    ETC: 'ETC',
} as const
export type ReceiptTypeType = Constant<typeof ReceiptType>

export const ExternalVoucherType = {
    // 1차 민생회복 소비쿠폰
    CSMRCUPN1: 'CSMRCUPN1',
    // 2차 민생회복 소비쿠폰
    CSMRCUPN2: 'CSMRCUPN2',
    // 특별 소비쿠폰
    CSMRCUPNS1: 'CSMRCUPNS1',
    // 정의되지 않은 코드
    ETC: 'ETC',
} as const

export type ExternalVoucherTypeType = Constant<typeof ExternalVoucherType>

export type RootState = {admissionDetail: AdmissionDetailState}

export * from './paymentMethod'
